import React from 'react';
import { Job, JobStatus } from '../../job-summary/Interfaces';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import { KTSVG } from '../../../../_metronic/helpers';
import { CustomerWithJobs } from '../utils/customerListTypes';
import { getBadgeColor } from '../utils/statusHelpers';
import JobRow from './JobRow';

export interface CustomerCardProps {
  customer: CustomerWithJobs;
  jobEmailsMap: Record<string, FeedEmailData[]>;
  hydratedCustomers: Record<string, boolean>;
  hydratingCustomer: string | null;
  customerHydrationErrors: Record<string, string>;
  updatingLastContactedFor: string | null;
  showStatusDropdown: string | null;
  currentSessionMinutes: number;
  formatDate: (dateInput: any) => string;
  formatTime: (minutes: number) => string;
  formatDueDate: (dueDate: string | undefined) => string;
  isJobActive: (job: Job) => boolean;
  onCardClick: () => void;
  onHydrate: () => void;
  onRefreshLastContacted: () => void;
  onEditJob: () => void;
  onJobClick: (e: React.MouseEvent<HTMLButtonElement>, job: Job) => void;
  onDateClick: (e: React.MouseEvent<HTMLElement>, job: Job) => void;
  onOpenStatusModal: (job: Job, e: React.MouseEvent) => void;
  onStatusDropdownClose: () => void;
  onStatusChange: (status: JobStatus) => void;
  onEmailClick: (email: FeedEmailData, e: React.MouseEvent) => void;
  onViewAllJobs: () => void;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  jobEmailsMap,
  hydratedCustomers,
  hydratingCustomer,
  customerHydrationErrors,
  updatingLastContactedFor,
  showStatusDropdown,
  currentSessionMinutes,
  formatDate,
  formatTime,
  formatDueDate,
  isJobActive,
  onCardClick,
  onHydrate,
  onRefreshLastContacted,
  onEditJob,
  onJobClick,
  onDateClick,
  onOpenStatusModal,
  onStatusDropdownClose,
  onStatusChange,
  onEmailClick,
  onViewAllJobs,
}) => {
  const badgeColor = getBadgeColor(customer);
  const hasStatusDropdownOpen = customer.activeJobs.some((job) => showStatusDropdown === job.id);

  return (
    <div className="col-lg-6 col-md-6 col-sm-12">
      <div
        className="card card-flush h-100 card-hover position-relative cursor-pointer bg-body shadow-sm border border-gray-300 border-dashed"
        style={{
          transition: 'all 0.15s ease',
          backdropFilter: 'blur(10px)',
          zIndex: hasStatusDropdownOpen ? 10000 : 'auto',
        }}
        onClick={onCardClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        <div className="card-hover-overlay" />

        {/* Header */}
        <div className="card-header border-0 pt-8 pb-4">
          <div className="w-100">
            <div className="d-flex align-items-start mb-3">
              <div className="symbol symbol-60px me-4">
                <div
                  className="symbol-label fs-1 fw-bold text-white"
                  style={{ backgroundColor: badgeColor }}
                >
                  {customer.name.split(' ').map((n) => n[0]).join('').toUpperCase()}
                </div>
              </div>
              <div className="flex-grow-1">
                <div className="d-none d-md-flex align-items-start justify-content-between">
                  <div>
                    <h4 className="fw-bold mb-2">{customer.name}</h4>
                    <span className="text-gray-600 fw-semibold fs-6">{customer.email}</span>
                  </div>
                  <div className="d-flex flex-column align-items-end">
                    <div className="d-flex align-items-center mb-1">
                      <span className="text-gray-800 fw-semibold fs-7 me-2">Last Contact</span>
                      <div className="btn-group" role="group">
                        <button
                          className={`btn btn-icon btn-sm ${hydratedCustomers[customer.id] ? 'btn-light-success' : 'btn-light-primary'} btn-active-primary`}
                          style={{ width: '24px', height: '24px', transition: 'all 0.15s ease' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onHydrate();
                          }}
                          disabled={hydratingCustomer === customer.id}
                          title={
                            hydratedCustomers[customer.id]
                              ? 'Email history enabled for this customer'
                              : 'Load full email history for this customer (persists on reload)'
                          }
                        >
                          {hydratingCustomer === customer.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : hydratedCustomers[customer.id] ? (
                            <i className="fas fa-check fs-9" />
                          ) : (
                            <i className="fas fa-history fs-9" />
                          )}
                        </button>
                        <button
                          className="btn btn-icon btn-sm btn-light-primary btn-active-primary"
                          style={{ width: '24px', height: '24px', transition: 'all 0.15s ease' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRefreshLastContacted();
                          }}
                          disabled={updatingLastContactedFor === customer.id}
                          title="Refresh last contacted date from latest email"
                          onMouseEnter={(e) => {
                            if (!updatingLastContactedFor) e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {updatingLastContactedFor === customer.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : (
                            <KTSVG path="/media/icons/duotune/communication/com004.svg" className="svg-icon-6" />
                          )}
                        </button>
                        <button
                          className="btn btn-icon btn-sm btn-light-primary btn-active-primary"
                          style={{ width: '24px', height: '24px', transition: 'all 0.15s ease' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditJob();
                          }}
                          title="Edit First Job"
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <KTSVG path="/media/icons/duotune/art/art005.svg" className="svg-icon-6" />
                        </button>
                      </div>
                    </div>
                    <span className="text-gray-600 fs-7 text-end">{formatDate(customer.lastContacted)}</span>
                    {customerHydrationErrors[customer.id] && (
                      <span className="text-danger fs-8 text-end mt-1">{customerHydrationErrors[customer.id]}</span>
                    )}
                  </div>
                </div>

                <div className="d-md-none">
                  <h4 className="fw-bold mb-2">{customer.name}</h4>
                  <span className="text-gray-600 fw-semibold fs-6 d-block mb-3">{customer.email}</span>
                </div>
              </div>
            </div>

            {/* Mobile Last Contact section */}
            <div className="d-md-none">
              <div className="d-flex align-items-center justify-content-between p-3 bg-light-primary rounded">
                <div className="d-flex align-items-center">
                  <span className="text-gray-800 fw-semibold fs-7 me-2">Last Contact:</span>
                  <span className="text-gray-600 fs-7">{formatDate(customer.lastContacted)}</span>
                </div>
                <div className="btn-group" role="group">
                  <button
                    className={`btn btn-icon btn-sm ${hydratedCustomers[customer.id] ? 'btn-light-success' : 'btn-light-primary'} btn-active-primary`}
                    style={{ width: '24px', height: '24px', transition: 'all 0.15s ease' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onHydrate();
                    }}
                    disabled={hydratingCustomer === customer.id}
                    title={
                      hydratedCustomers[customer.id]
                        ? 'Email history enabled for this customer'
                        : 'Load full email history for this customer (persists on reload)'
                    }
                  >
                    {hydratingCustomer === customer.id ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    ) : hydratedCustomers[customer.id] ? (
                      <i className="fas fa-check fs-9" />
                    ) : (
                      <i className="fas fa-history fs-9" />
                    )}
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light-primary btn-active-primary"
                    style={{ width: '24px', height: '24px', transition: 'all 0.15s ease' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRefreshLastContacted();
                    }}
                    disabled={updatingLastContactedFor === customer.id}
                    title="Refresh last contacted date from latest email"
                    onMouseEnter={(e) => {
                      if (!updatingLastContactedFor) e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {updatingLastContactedFor === customer.id ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    ) : (
                      <KTSVG path="/media/icons/duotune/communication/com004.svg" className="svg-icon-6" />
                    )}
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light-primary btn-active-primary"
                    style={{ width: '24px', height: '24px', transition: 'all 0.15s ease' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditJob();
                    }}
                    title="Edit First Job"
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <KTSVG path="/media/icons/duotune/art/art005.svg" className="svg-icon-6" />
                  </button>
                </div>
              </div>
              {customerHydrationErrors[customer.id] && (
                <div className="text-danger fs-8 mt-2">{customerHydrationErrors[customer.id]}</div>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="card-body pt-0 pb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-semibold text-gray-800 fs-6 mb-0">
              <i className="bi bi-briefcase me-2" />
              Active Jobs
            </h6>
            <div className="d-flex align-items-center gap-2">
              <span className="badge badge-light-info fs-7">
                {customer.activeJobs.length} job{customer.activeJobs.length !== 1 ? 's' : ''}
              </span>
              {customer.activeJobs.length > 1 && (
                <button
                  className="btn btn-sm btn-light-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewAllJobs();
                  }}
                  title="View All Jobs"
                >
                  <span className="fw-bold">+{customer.activeJobs.length - 1}</span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex flex-column gap-3">
              {customer.activeJobs.map((job) => {
                const jobEmails = jobEmailsMap[job.id] || [];
                return (
                  <JobRow
                    key={job.id}
                    job={job}
                    emails={jobEmails}
                    isJobActive={isJobActive(job)}
                    currentSessionMinutes={currentSessionMinutes}
                    formatTime={formatTime}
                    formatDueDate={formatDueDate}
                    statusDropdownOpen={showStatusDropdown === job.id}
                    onJobClick={onJobClick}
                    onDateClick={onDateClick}
                    onStatusDropdownTriggerClick={(e) => {
                      e.stopPropagation();
                      if (showStatusDropdown === job.id) {
                        onStatusDropdownClose();
                      } else {
                        onOpenStatusModal(job, e);
                      }
                    }}
                    onStatusChange={onStatusChange}
                    onEmailClick={onEmailClick}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCard;
